(ns practitest.frontend.login.events
  (:require
   [ajax.core :as ajax]
   [clojure.string :as str]
   [practitest.frontend.common.dom :as dom]
   [practitest.frontend.common.i18n :refer [get-translation]]
   [practitest.frontend.routing.backend-routes :as broutes]
   [practitest.frontend.routing.router :as router]
   [re-frame.core :as re-frame]))

(re-frame/reg-fx
  :navigate
  dom/navigate!)

(re-frame/reg-event-db
  ::init
  (fn [db _]
    (assoc db :login {:step                 :email
                      :email                (get-in db [:login :email] "")
                      :password             nil
                      :error                nil
                      :loading?             false
                      :mfa-details          nil
                      :forgot-password-sent? (get-in db [:login :forgot-password-sent?] false)})))

(re-frame/reg-event-fx
  ::set-password-step
  (fn [{:keys [db]} _]
    (if (str/blank? (get-in db [:login :email]))
      ;; No email — shouldn't be here, send back to email step
      {:dispatch [::router/replace-state :login]}
      {:db (-> db
               (assoc-in [:login :step] :password)
               (assoc-in [:login :error] nil))})))

(re-frame/reg-event-db
  ::init-forgot
  (fn [db _]
    (-> db
        (assoc-in [:login :forgot-password-sent?] false)
        (assoc-in [:login :error] nil)
        (assoc-in [:login :loading?] false))))

(re-frame/reg-event-db
  ::set-email
  (fn [db [_ email]]
    (assoc-in db [:login :email] email)))


(re-frame/reg-event-db
  ::set-mfa-code
  (fn [db [_ code]]
    (assoc-in db [:login :mfa-code] code)))

(re-frame/reg-event-fx
  ::check-sso
  (fn [{:keys [db]} [_ email]]
    {:db (-> db
             (assoc-in [:login :loading?] true)
             (assoc-in [:login :error] nil)
             (assoc-in [:login :email] email))
     :http-xhrio {:method          :get
                  :uri             (broutes/get-url "sessions#check_sso")
                  :params          {:email email}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::check-sso-success]
                  :on-failure      [::request-failure]}}))

(re-frame/reg-event-fx
  ::check-sso-success
  (fn [{:keys [db]} [_ response]]
    (if (:sso response)
      ;; SSO: redirect browser to Cognito
      {:db (assoc-in db [:login :loading?] false)
       :navigate (:sso_url response)}
      ;; Password: navigate to /login/password — gives browser a history entry to go back from
      {:db (assoc-in db [:login :loading?] false)
       :dispatch [::router/push-state :login-password]})))

(re-frame/reg-event-fx
  ::submit-login
  (fn [{:keys [db]} [_ password remember-me?]]
    (let [email (get-in db [:login :email])]
      {:db (-> db
               (assoc-in [:login :loading?] true)
               (assoc-in [:login :error] nil))
       :http-xhrio {:method          :post
                    :uri             (str (broutes/get-url "sessions#create") ".json")
                    :params          {:email       email
                                      :password    password
                                      :remember_me (if remember-me? "1" "0")}
                    :format          (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [::login-success]
                    :on-failure      [::login-failure]}})))

(re-frame/reg-event-fx
  ::login-success
  (fn [{:keys [db]} [_ response]]
    (cond
      ;; MFA required
      (:mfa_required response)
      {:db (-> db
               (assoc-in [:login :loading?] false)
               (assoc-in [:login :step] :mfa)
               (assoc-in [:login :mfa-details] (:mfa_details response)))}

      ;; Full success — navigate to redirect URL (full page reload to boot authenticated app)
      (:success response)
      {:db (assoc-in db [:login :loading?] false)
       :navigate (:redirect_to response)}

      :else
      {:db (-> db
               (assoc-in [:login :loading?] false)
               (assoc-in [:login :error] (get-translation "login.error.login-failed" "Login failed")))})))

(re-frame/reg-event-fx
  ::login-failure
  (fn [{:keys [db]} [_ {:keys [response]}]]
    (cond
      (= (:error_type response) "no_project")
      {:db       (-> db
                     (assoc-in [:login :loading?] false)
                     (assoc-in [:login :other-datacenters] (:other_datacenters response)))
       :dispatch [::router/push-state :no-project]}

      (= (:error_type response) "user_without_project")
      {:db       (-> db
                     (assoc-in [:login :loading?] false)
                     (assoc-in [:options :user-without-project-data] (:user_without_project_data response)))
       :dispatch [::router/push-state :user-without-project]}

      (:redirect_to response)
      {:db      (assoc-in db [:login :loading?] false)
       :navigate (:redirect_to response)}

      :else
      {:db (-> db
               (assoc-in [:login :loading?] false)
               (assoc-in [:login :error] (or (:error response) (get-translation "login.error.invalid-credentials" "Invalid email or password"))))})))

(re-frame/reg-event-fx
  ::submit-mfa
  (fn [{:keys [db]} _]
    (let [{:keys [mfa-code mfa-details]} (:login db)]
      (if (nil? mfa-details)
        {:dispatch [::router/replace-state :login]}
        {:db (-> db
                 (assoc-in [:login :loading?] true)
                 (assoc-in [:login :error] nil))
         :http-xhrio {:method          :post
                      :uri             (str (broutes/get-url "sessions#login_mfa") ".json")
                      :params          {:code              mfa-code
                                        :challenge_name    (:challenge_name mfa-details)
                                        :challenge_session (:challenge_session mfa-details)
                                        :challenge_user_id (:challenge_user_id mfa-details)
                                        :user_id           (:user_id mfa-details)}
                      :format          (ajax/json-request-format)
                      :response-format (ajax/json-response-format {:keywords? true})
                      :on-success      [::mfa-success]
                      :on-failure      [::login-failure]}}))))

(re-frame/reg-event-fx
  ::mfa-success
  (fn [{:keys [db]} [_ response]]
    {:db (assoc-in db [:login :loading?] false)
     :navigate (:redirect_to response "/")}))

(re-frame/reg-event-fx
  ::google-login
  (fn [{:keys [db]} _]
    {:navigate (get-in db [:options :cognito-google-url])}))

(re-frame/reg-event-db
  ::back-to-email
  (fn [db _]
    (-> db
        (assoc-in [:login :step] :email)
        (assoc-in [:login :error] nil))))

(re-frame/reg-event-fx
  ::submit-forgot-password
  (fn [{:keys [db]} [_ email]]
    {:db (-> db
             (assoc-in [:login :loading?] true)
             (assoc-in [:login :error] nil))
     :http-xhrio {:method          :post
                  :uri             (str (broutes/get-url "sessions#forgot_password") ".json")
                  :params          {:user {:email email}}
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::forgot-password-success]
                  :on-failure      [::forgot-password-failure]}}))

(re-frame/reg-event-fx
  ::forgot-password-success
  (fn [{:keys [db]} [_ _response]]
    {:db       (-> db
                   (assoc-in [:login :loading?] false)
                   (assoc-in [:login :forgot-password-sent?] true)
                   (assoc-in [:login :error] nil))
     :dispatch [::router/push-state :login]}))

(re-frame/reg-event-fx
  ::forgot-password-failure
  (fn [{:keys [db]} [_ {:keys [response status]}]]
    {:db (-> db
             (assoc-in [:login :loading?] false)
             (assoc-in [:login :error]
                       (if (= status 429)
                         (get-translation "login.error.reset-throttled" "Please wait before requesting another reset email")
                         (or (:error response) (get-translation "login.error.reset-failed" "Failed to send reset email")))))}))

(re-frame/reg-event-fx
  ::request-failure
  (fn [{:keys [db]} [_ {:keys [response]}]]
    {:db (-> db
             (assoc-in [:login :loading?] false)
             (assoc-in [:login :error] (or (:error response) (get-translation "login.error.generic" "Something went wrong, please try again"))))}))
